import { ChargeEvent } from "@fairfleet/geotab";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

import i18n from "@/i18n";

export default function ChargeEventDetails({ chargeEvents }: { chargeEvents: ChargeEvent[] }) {
  const { t } = useTranslation();
  const round = (value: number) => Math.max(0, Math.round(value * 10) / 10);

  const formatDate = (date: Date) =>
    new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(date));
  const getProgressWidth = (startSoC: number, endSoC: number) => ((endSoC - startSoC) * 100) / 100;

  if (!chargeEvents || !(chargeEvents.length > 0)) return <></>;
  return (
    <>
      <Typography variant="h6">{t("modal.chargeEventDetails")}</Typography>
      {chargeEvents && chargeEvents.length > 0 ? (
        <Grid container spacing={2} mt={2}>
          {chargeEvents.map((event, index) => (
            <Grid item xs={12} key={index}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <strong>{formatDate(event.startTime)}</strong>
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                      <Box sx={{ position: "relative", width: "90%", mx: "auto", height: "30px" }}>
                        {/* Loadingbar*/}
                        <Box
                          sx={{
                            width: "100%",
                            height: 15,
                            backgroundColor: "#f0f0f0",
                            borderRadius: "10px",
                            position: "relative",
                            overflow: "hidden",
                            mt: 2,
                          }}
                        >
                          {/* blue part (already charged) */}
                          <Tooltip title={t("modal.alreadyCharged")}>
                            <Box
                              sx={{
                                position: "absolute",
                                left: 0,
                                width: `${event.endStateOfCharge}%`,
                                height: "100%",
                                backgroundColor: "primary.main",
                                borderRadius: "10px",
                              }}
                            />
                          </Tooltip>

                          {/* Green part (new charged) */}

                          <Tooltip title={t("modal.newCharged")}>
                            <Box
                              sx={{
                                position: "absolute",
                                left: `${event.startStateOfCharge}%`,
                                width: `${getProgressWidth(event.startStateOfCharge, event.endStateOfCharge)}%`,
                                height: "100%",
                                backgroundColor: "success.main",
                                borderRadius: "0 10px 10px 0",
                              }}
                            />
                          </Tooltip>
                        </Box>
                        {/* Start of the green bar*/}
                        <Tooltip title={t("modal.startSoc")}>
                          <Typography
                            variant="body2"
                            sx={{
                              position: "absolute",
                              left: `${event.startStateOfCharge}%`,
                              top: "25px",
                              transform: "translateX(-25%)",
                              fontWeight: "bold",
                              textWrap: "nowrap",
                            }}
                          >
                            {`${round(event.startStateOfCharge)} %`}
                          </Typography>
                        </Tooltip>
                        {/* end of the green bar*/}
                        <Tooltip title={t("modal.endSoc")}>
                          <Typography
                            variant="body2"
                            sx={{
                              position: "absolute",
                              left: `${event.endStateOfCharge}%`,
                              top: "-25px",
                              transform: "translateX(-25%)",
                              fontWeight: "bold",
                              textWrap: "nowrap",
                            }}
                          >
                            {`${round(event.endStateOfCharge)} %`}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Grid>
                    {/* Icons for Energy Consumed, Duration, and Peak Power in one row */}
                    <Grid item xs={4} display="flex" alignItems="center" justifyContent="center">
                      <Tooltip title={t("modal.energyConsumed")}>
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                          <BatteryChargingFullIcon sx={{ marginRight: 1 }} />
                          <Typography variant="body2" align="center">
                            {`${round(event.energyConsumedKwh)} kWh`}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={4} display="flex" alignItems="center" justifyContent="center">
                      <Tooltip title={t("modal.duration")}>
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                          <AccessTimeIcon sx={{ marginRight: 1 }} />
                          <Typography variant="body2" align="center">
                            {`${event.duration.split(".")[0]}`}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={4} display="flex" alignItems="center" justifyContent="center">
                      <Tooltip title={t("modal.peakPower")}>
                        <Box sx={{ display: "flex", flexDirection: "row" }}>
                          <FlashOnIcon sx={{ marginRight: 1 }} />
                          <Typography variant="body2" align="center">
                            {`${round(event.peakPowerKw)} kW`}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography variant="body2">{t("modal.noChargeEvents")}</Typography>
      )}
    </>
  );
}
